import React, { useCallback, useEffect, useState } from 'react';
import { makeVar, useReactiveVar } from '@apollo/client';
import { Metrics, ConfigType as MetricsConfig } from '@cvent/sli-nextjs-metrics';
import transformDeprecatedDatadogTag from '@metrics/transformDeprecatedDatadogTags';
import { getDatadogReporter } from '@metrics/datadogReporter';
import { analyticsReporter } from '@metrics/analyticsReporter';
import { rudderStackReporter } from '@metrics/rudderstackReporter';
import { initRudderStack } from '@metrics/client/initRudderstack';
import { BUSINESS_UNIT, COMPONENT } from '@metrics/constants';
import getConfig from 'next/config';
import { AttendeeLoggedIn, Persona, UserGroup } from './analytics-node';
import isSyntheticTest from '@metrics/isSyntheticTest';

const { publicRuntimeConfig } = getConfig();
const clientVersion = String(publicRuntimeConfig.CLIENT_VERSION) || 'unknown';
/**
 * Reactive variable. If you are not using @apollo/client to manage state in your application
 * you will have to manage how to access whether or not the metrics client has been initialized
 * on your own (See line 218).
 */
export const appConfigsVar = makeVar<Record<string, unknown>>({});

interface MetricsProps {
  environment: string;
  userId?: string;
  persona: Persona;
  rudderstackWriteKey: string;
  rudderstackDataPlaneUrl: string;
  isAutomationAccount: boolean;
  isCVIIUser?: boolean;
  appBaseProps?: Record<string, unknown>;
  children: JSX.Element;
}

interface AppMetricsProps {
  isAutomationAccount: boolean;
  metricsEnabled?: boolean;
  children: JSX.Element;
  environmentName: string;
  baseData: Record<string, unknown>;
  onMetricsInit?: (instance: any) => any;
  cdpWriteKey?: string;
  cdpDataPlaneUrl?: string;
}

/**
 * Application Metrics Component. Tracks application metrics to multiple destinations
 * including Data Dog, MixPanel, Intercom, etc via the Rudderstack SDK
 *
 * Note: Each reporter is responsible for checking the destination property passed in
 * the data of each action
 *
 *
 * @param param0
 * @returns
 */
export const AppMetrics = ({
  children,
  environmentName,
  baseData,
  isAutomationAccount,
  onMetricsInit = (instance: any) => instance,
  cdpWriteKey,
  cdpDataPlaneUrl,
  metricsEnabled = true
}: AppMetricsProps): JSX.Element => {
  const [rudderstackInitialized, setRudderstackInitialized] = useState(false);

  useEffect(() => {
    if (!rudderstackInitialized) {
      initRudderStack({
        metricsEnabled,
        writeKey: cdpWriteKey,
        dataPlaneUrl: cdpDataPlaneUrl,
        environmentName,
        onLoaded: rdInstance => {
          setRudderstackInitialized(true);
          onMetricsInit(rdInstance);
        }
      });
    }
  }, [
    metricsEnabled,
    cdpWriteKey,
    cdpDataPlaneUrl,
    environmentName,
    onMetricsInit,
    rudderstackInitialized,
    setRudderstackInitialized
  ]);

  const config: MetricsConfig = {
    environmentName,
    metricsEnabled,
    skipDeprecatedDefaultPrefix: true
  };
  // we will ignore adding Rudderstack reporter if we're dealing with automation tests data
  const reporters = [getDatadogReporter(baseData), analyticsReporter];
  if (!isAutomationAccount && !isSyntheticTest()) {
    reporters.push(rudderStackReporter);
  }

  return (
    <Metrics config={config} baseData={baseData} reporters={reporters} delegateLogsToReporters>
      {children}
    </Metrics>
  );
};

/**
 * Metrics Provider. Sets up the application with performance and analytics metrics functionality
 * via the page actions api with CDP support. This component is intended to wrap your application's
 * main entry point component (usually the app.tsx file under the pages directory for nextJS apps)
 *
 * @param param0
 * @returns
 */
export default function MetricsProvider({
  environment,
  userId,
  persona,
  isAutomationAccount,
  rudderstackWriteKey,
  rudderstackDataPlaneUrl,
  isCVIIUser = false,
  appBaseProps = {},
  children
}: MetricsProps): JSX.Element {
  const appConfigs = useReactiveVar(appConfigsVar);

  // the base data we need to transform to match tracing standards and be backwards compatible
  // the rest of attributes added below do not need to be transformed
  const backwardsCompatibleData = {
    ...appBaseProps,
    // The current application version. Value should be grabbed from your app's package.json file
    appVersion: clientVersion,
    environmentName: environment
  };

  // If this is an automation account we set it as automation else as unknown. The CDP tool will update this accordingly
  // when the user is identified
  const userGroup = isAutomationAccount || isSyntheticTest() ? UserGroup.Automation : UserGroup.Unknown;

  // Application's metrics base properties. These properties will be sent as metadata for each action tracked.
  const baseData = {
    // the base data we need to transform and keep the backwards compatibility
    ...transformDeprecatedDatadogTag(backwardsCompatibleData),
    trackingPlanName: 'Attendee Hub',
    persona,
    platform: publicRuntimeConfig.ANALYTICS_PLATFORM,
    // Identifier to reference the business unit in the downstream tools. Visit the Product Breakdown wiki to learn more
    businessUnit: BUSINESS_UNIT,
    // Unique identifier to reference the application that is sending the action to the downstream tools
    component: COMPONENT,
    isCVIIUser,
    userId,
    ddUserId: userId,
    userGroup
  };

  const metricsEnabled = publicRuntimeConfig.ENABLE_METRICS === 'true';
  const onMetricsInit = useCallback(() => {
    // It is safe to always check that metrics have been initialized before using the analytic's api. It is stored in the
    // appConfigs reactive variable so that it is easy to retrieve anywhere within the application
    appConfigsVar({ ...appConfigs, metricsInitialized: true });
  }, [appConfigs]);

  return (
    <AppMetrics
      isAutomationAccount={isAutomationAccount}
      metricsEnabled={metricsEnabled}
      environmentName={environment}
      baseData={baseData}
      cdpWriteKey={rudderstackWriteKey}
      cdpDataPlaneUrl={rudderstackDataPlaneUrl}
      onMetricsInit={onMetricsInit}
    >
      {children}
    </AppMetrics>
  );
}

export { Persona, UserGroup };
export type { AttendeeLoggedIn };
